// prebuiltAppSetsCalculator.js
// Transforms API response cumulativeData into PrebuiltSets component format
// Based on domain calculator with app-specific fields and calculated_bid_guidance formula
// UPDATED: Divide calculated_bid_guidance by 1000 before calculations
// FIXED: Market requests and downloads calculation for LLM search results
// CRITICAL FIX: Ensure marketRequests and totalDownloads are properly attached to final objects

import { relevanceFromDistance } from "../utils";

/**
 * Main calculator function with enhanced debugging - UPDATED
 */
export const calculatePrebuiltAppSets = (apiResponse) => {
	try {
		const body = apiResponse?.body || apiResponse?.data?.body || apiResponse;

		if (!body) {
			console.warn("No body found in API response");
			return {};
		}

		const { data: apps = [], cumulativeData = [], keywords = [] } = body;

		if (!apps.length || !cumulativeData.length) {
			console.warn("No apps or cumulative data found");
			return {};
		}

		console.log(
			"📊 DEBUGGING: Sample app data for market requests calculation:",
			{
				appCount: apps.length,
				sampleApp: apps[0]
					? {
							totalCprMarketRequests: apps[0].totalCprMarketRequests,
							totalMarketRequests: apps[0].totalMarketRequests,
							downloads: apps[0].downloads,
							downloadsType: typeof apps[0].downloads,
						}
					: "No apps",
			},
		);

		// Transform each segment into a prebuilt set
		const prebuiltSets = {};

		cumulativeData.forEach((segment, index) => {
			const setKey = generateSetKey(segment.segment);
			const segmentApps = getAppsForSegment(segment, apps);

			if (segmentApps.length === 0) {
				return;
			}

			console.log(`📊 DEBUGGING: Calculating metrics for ${segment.segment}:`, {
				appCount: segmentApps.length,
				sampleMarketRequests: segmentApps[0]?.totalCprMarketRequests,
				sampleDownloads: segmentApps[0]?.downloads,
			});

			// Calculate aggregated metrics
			const metrics = calculateAggregatedMetrics(segmentApps);
			const categories = extractUniqueCategories(segmentApps);
			const performanceScore = calculateAveragePerformanceScore(segmentApps);

			// FIXED: Calculate market requests and downloads
			const marketRequests = calculateTotalMarketRequests(segmentApps);
			const totalDownloads = calculateTotalDownloads(segmentApps);

			console.log(`📊 DEBUGGING: Calculated values for ${segment.segment}:`, {
				marketRequests,
				totalDownloads,
				rawMarketRequestsSum: segmentApps.reduce(
					(sum, app) => sum + (app.totalCprMarketRequests || 0),
					0,
				),
				rawDownloadsSum: segmentApps.reduce((sum, app) => {
					let downloads = app.downloads || 0;
					if (typeof downloads === "string") {
						const numStr = downloads.toLowerCase();
						let multiplier = 1;
						const baseValue = parseFloat(numStr);
						if (numStr.includes("m")) multiplier = 1000000;
						else if (numStr.includes("k")) multiplier = 1000;
						downloads = (baseValue || 0) * multiplier;
					}
					return sum + (downloads || 0);
				}, 0),
			});

			// CRITICAL FIX: Create the set object with explicit marketRequests and totalDownloads
			const setObject = {
				id: `prebuilt_${setKey}_${Date.now()}`,
				name: formatSegmentName(segment.segment),
				description: generateDescription(segment.segment, segmentApps.length),
				category: "Generated",
				apps: segmentApps.map((app) => formatAppForSet(app)),

				// CRITICAL: These are the missing values that need to be displayed in UI
				marketRequests: marketRequests, // e.g., '283.9M'
				totalDownloads: totalDownloads, // e.g., '40.0M'

				metrics: {
					ctrVtr: `${metrics.avgCtr}/${metrics.avgVtr}`,
					ctr: {
						value: metrics.avgCtr,
						trend: "up",
					},
					viewability: {
						value: metrics.avgViewability,
					},
					bidGuidance: {
						value: metrics.avgBidGuidance,
					},
					vtr: {
						value: metrics.avgVtr,
					},
					completionRate: {
						value: metrics.avgCompletionRate,
						trend: "up",
					},
					avgDownloadsPerDay: formatLargeNumber(segment.reach || 0),

					// CRITICAL FIX: Add the calculated values to metrics as backup
					marketRequests: marketRequests,
					totalDownloads: totalDownloads,
				},

				// ADDITIONAL BACKUP: Add to summary object
				summary: {
					marketRequests: marketRequests,
					totalDownloads: totalDownloads,
				},

				// Additional properties
				filters: {},
				createdAt: new Date().toISOString(),
				source: "frontend_cumulative",
				metadata: {
					totalApps: segmentApps.length,
					generated: true,
					isBackendGenerated: false,
					timestamp: Date.now(),
					source: "frontend_cumulative",
				},

				// Legacy properties for compatibility
				performanceScore: performanceScore,
				categories: categories.slice(0, 3),
				segmentData: segment,
				rawApps: segmentApps,
				cumulativeData: segment,
			};

			// DEBUGGING: Log the final object structure
			console.log(`📊 FINAL SET OBJECT for ${segment.segment}:`, {
				name: setObject.name,
				marketRequests: setObject.marketRequests,
				totalDownloads: setObject.totalDownloads,
				hasMarketRequests: !!setObject.marketRequests,
				hasTotalDownloads: !!setObject.totalDownloads,
			});

			prebuiltSets[setKey] = setObject;
		});

		console.log(
			"📊 FINAL DEBUGGING: Prebuilt sets created:",
			Object.keys(prebuiltSets).map((key) => ({
				key: key,
				name: prebuiltSets[key].name,
				apps: prebuiltSets[key].apps.length,
				marketRequests: prebuiltSets[key].marketRequests,
				totalDownloads: prebuiltSets[key].totalDownloads,
				hasMarketRequests: !!prebuiltSets[key].marketRequests,
				hasTotalDownloads: !!prebuiltSets[key].totalDownloads,
			})),
		);

		return prebuiltSets;
	} catch (error) {
		console.error("Error calculating prebuilt app sets:", error);
		return {};
	}
};

/**
 * Generate a unique key for the set
 */
const generateSetKey = (segmentName) => {
	return segmentName
		.toLowerCase()
		.replace(/\s+/g, "_")
		.replace(/[^a-z0-9_]/g, "");
};

/**
 * Get apps for a specific segment
 */
const getAppsForSegment = (segment, allApps) => {
	if (!segment.domainIds) return [];

	let appIds = [];

	if (Array.isArray(segment.domainIds)) {
		// New format: domainIds is already an array
		appIds = segment.domainIds;
	} else if (typeof segment.domainIds === "string") {
		// Old format: domainIds is a string representation of array
		const cleanAppIds = segment.domainIds.replace(/^\[/, "").replace(/\]$/, "");
		appIds = cleanAppIds.split(",").map((id) => id.trim());
	}

	// NO DEDUPLICATION - RETURN ALL MATCHING APPS
	const foundApps = allApps.filter((app) => {
		return appIds.some(
			(id) =>
				app.id === id ||
				String(app.id) === String(id) ||
				Number(app.id) === Number(id),
		);
	});

	return foundApps;
};

/**
 * Calculate average downloads per day with proper formatting
 */
const calculateAvgDownloadsPerDay = (segment, apps) => {
	// Use the reach directly from the cumulative data
	const segmentReach = segment.reach || 0;

	// If you want to calculate daily downloads, you'd need a time period
	// For now, return the total reach from the segment
	return segmentReach;
};

/**
 * Calculate aggregated metrics for a set of apps using correct formulas
 * UPDATED: Now calculates average bid guidance (divided by 1000) instead of revenue per impression
 */
const calculateAggregatedMetrics = (apps) => {
	if (!apps.length) return getDefaultMetrics();

	// CTR calculation
	const totalClicks = apps.reduce(
		(sum, app) => sum + (app.totalCprClicks || 0),
		0,
	);
	const totalImpressionsWithClickMacro = apps.reduce(
		(sum, app) => sum + (app.totalImpressionsWithClickMacro || 0),
		0,
	);

	const aggregatedCTR =
		totalImpressionsWithClickMacro > 0
			? totalClicks / totalImpressionsWithClickMacro
			: 0;

	// Completion rate calculation
	const totalCompleteFirst = apps.reduce(
		(sum, app) => sum + (app.totalCompleteFirstOccurrence || 0),
		0,
	);
	const totalStartFirst = apps.reduce(
		(sum, app) => sum + (app.totalStartFirstOccurrence || 0),
		0,
	);

	const aggregatedCompletionRate =
		totalStartFirst > 0 ? (totalCompleteFirst * 100.0) / totalStartFirst : 0;

	// VTR calculation using correct aggregated formula
	const aggregatedVTR =
		totalStartFirst > 0 ? (totalCompleteFirst / totalStartFirst) * 100 : 0;

	// Viewability calculation using correct aggregated formula
	const totalViewables = apps.reduce(
		(sum, app) => sum + (app.totalViewables || 0),
		0,
	);
	const totalViewabilityMeasures = apps.reduce(
		(sum, app) => sum + (app.totalViewabilityMeasures || 0),
		0,
	);
	const aggregatedViewability =
		totalViewabilityMeasures > 0
			? (totalViewables / totalViewabilityMeasures) * 100
			: 0;

	// ✅ UPDATED: Calculate average bid guidance (DIVIDE BY 1000 FIRST)
	const validBidGuidanceApps = apps.filter(
		(app) =>
			app.calculated_bid_guidance !== undefined &&
			app.calculated_bid_guidance !== null &&
			!isNaN(parseFloat(app.calculated_bid_guidance)),
	);

	const avgBidGuidance =
		validBidGuidanceApps.length > 0
			? validBidGuidanceApps.reduce((sum, app) => {
					// ✅ DIVIDE BY 1000 FIRST before adding to sum
					const bidGuidanceValue =
						parseFloat(app.calculated_bid_guidance) / 1000000;
					return sum + bidGuidanceValue;
				}, 0) / validBidGuidanceApps.length
			: 0;

	return {
		avgCtr: `${aggregatedCTR.toFixed(3)}%`,
		avgViewability: `${aggregatedViewability.toFixed(1)}%`,
		avgBidGuidance: `$${avgBidGuidance.toFixed(3)}`, // ✅ UPDATED: Now calculates average bid guidance (divided by 1000)
		avgVtr: `${aggregatedVTR.toFixed(1)}%`,
		avgCompletionRate: `${aggregatedCompletionRate.toFixed(1)}%`,
	};
};

/**
 * Get default metrics when no data is available
 * UPDATED: Changed from avgRevenuePerImp to avgBidGuidance
 */
const getDefaultMetrics = () => ({
	avgCtr: "0.000%",
	avgViewability: "0.0%",
	avgBidGuidance: "$0.000", // ✅ UPDATED: Changed from avgRevenuePerImp
	avgVtr: "0.0%",
	avgCompletionRate: "0.0%",
});

/**
 * Calculate average performance score
 */
const calculateAveragePerformanceScore = (apps) => {
	if (!apps.length) return 75;

	// Step 1: Calculate individual relevance score for each app using the Apps sigmoid
	const individualPerformanceScores = [];

	apps.forEach((app, index) => {
		const appScore = app?.score;

		if (
			appScore !== undefined &&
			appScore !== null &&
			!isNaN(parseFloat(appScore))
		) {
			// Apps sigmoid relevance curve (see constants.js)
			const score = parseFloat(appScore);
			individualPerformanceScores.push(relevanceFromDistance(score, "APPS"));
		}
	});

	// Step 2: Calculate the average of all individual performance scores
	if (individualPerformanceScores.length === 0) {
		return 85;
	}

	const sumOfScores = individualPerformanceScores.reduce(
		(total, score) => total + score,
		0,
	);
	const averagePerformanceScore =
		sumOfScores / individualPerformanceScores.length;
	const finalScore = Math.round(averagePerformanceScore);
	return finalScore;
};

/**
 * Extract unique categories from apps
 */
const extractUniqueCategories = (apps) => {
	const categoryCount = {};

	apps.forEach((app) => {
		if (app.categories && Array.isArray(app.categories)) {
			app.categories.forEach((category) => {
				if (category && category.trim()) {
					categoryCount[category] = (categoryCount[category] || 0) + 1;
				}
			});
		} else {
			// Handle single category field
			const category = app.category || app.subcategory || app.genre;
			if (category && category.trim()) {
				categoryCount[category] = (categoryCount[category] || 0) + 1;
			}
		}
	});

	// Sort by frequency and return top categories
	return Object.entries(categoryCount)
		.sort(([, a], [, b]) => b - a)
		.map(([category]) => category);
};

/**
 * Calculate total downloads across apps - FIXED
 */
const calculateTotalDownloads = (apps) => {
	if (!apps.length) return "0";

	const totalDownloads = apps.reduce((sum, app) => {
		let downloads = app.downloads || 0;

		// Handle string formats like "1.00M", "50.00M"
		if (typeof downloads === "string") {
			const numStr = downloads.toLowerCase();
			let multiplier = 1;
			let baseValue = parseFloat(numStr);

			if (numStr.includes("k")) {
				multiplier = 1000;
			} else if (numStr.includes("m")) {
				multiplier = 1000000;
			} else if (numStr.includes("b")) {
				multiplier = 1000000000;
			} else {
				// Try to parse as plain number string
				baseValue = parseFloat(downloads.replace(/[^0-9.]/g, ""));
			}

			downloads = (baseValue || 0) * multiplier;
		}

		return sum + (downloads || 0);
	}, 0);

	return formatLargeNumber(totalDownloads);
};

/**
 * Format app for set display using correct formulas
 * UPDATED: Now includes calculated_bid_guidance (divided by 1000) instead of revenue per impression
 */
const formatAppForSet = (app) => {
	// CTR calculation
	const clicks = app.totalCprClicks || 0;
	const impressionsWithClickMacro = app.totalImpressionsWithClickMacro || 0;
	const ctrValue =
		impressionsWithClickMacro > 0
			? ((clicks / impressionsWithClickMacro) * 100).toFixed(3)
			: "0.000";

	// VTR calculation using correct formula
	const completeFirst = app.totalCompleteFirstOccurrence || 0;
	const startFirst = app.totalStartFirstOccurrence || 0;
	const vtrValue =
		startFirst > 0 ? ((completeFirst / startFirst) * 100).toFixed(1) : "0.0";

	// Completion rate calculation (same as VTR in this case)
	const completionRate =
		startFirst > 0 ? ((completeFirst / startFirst) * 100).toFixed(1) : "0.0";

	// Viewability calculation using correct formula
	const viewables = app.totalViewables || 0;
	const viewabilityMeasures = app.totalViewabilityMeasures || 0;
	const viewabilityValue =
		viewabilityMeasures > 0
			? ((viewables / viewabilityMeasures) * 100).toFixed(1)
			: "0.0";

	// ✅ UPDATED: Get bid guidance from app data and DIVIDE BY 1000
	const rawBidGuidanceValue = app.calculated_bid_guidance || 0;
	const bidGuidanceValue = rawBidGuidanceValue / 1000000; // ✅ DIVIDE BY 1000

	return {
		// Core identifiers
		id: app.id || app.bundle_id,
		bundle_id: app.bundle_id || app.bundleId,

		// App information
		app_name: app.app_name || app.name || "Unknown App",
		name: app.app_name || app.name || "Unknown App",
		developer: app.developer || app.creator || "Unknown Developer",

		// Categories and classification
		category: app.category || app.subcategory || "General",
		subcategory: app.subcategory || app.category,
		content_rating: app.content_rating || "Everyone",

		// Platform and market info
		platform: app.platform || "unknown",
		market_source: app.market_source || "api",

		// Ratings and reviews
		rating: app.rating || 0,
		rating_count: app.rating_count || 0,

		// Downloads and requests
		downloads: app.downloads || 0,
		totalCprMarketRequests: app.totalCprMarketRequests || 0,

		// ✅ UPDATED: Store calculated_bid_guidance (divided by 1000)
		calculated_bid_guidance: bidGuidanceValue,

		metrics: {
			ctrVtr: `${ctrValue}%/${vtrValue}%`,
			viewability: `${viewabilityValue}%`,
			bidGuidance: bidGuidanceValue
				? `${parseFloat(bidGuidanceValue).toFixed(3)}`
				: "N/A", // ✅ UPDATED: Using divided value
			categories: app.categories || [
				app.category || app.subcategory || "General",
			],
			developer: app.developer || app.creator || "Unknown",
			downloads: formatLargeNumber(app.downloads || 0),
			rating: app.rating ? app.rating.toFixed(1) : "N/A",
			performance: app.score
				? Math.round(relevanceFromDistance(app.score, "APPS"))
				: 85,
			vtr: `${vtrValue}%`,
			completionRate: `${completionRate}%`,
			clicks: clicks,
			impressions: app.totalImpressions || 0,
			impressionsWithClickMacro: impressionsWithClickMacro,
			rawCtr: parseFloat(app.calculated_ctr),
			rawVtr: parseFloat(vtrValue),
			rawCompletionRate: parseFloat(completionRate),
		},
	};
};

/**
 * Format segment name for display
 */
const formatSegmentName = (segmentName) => {
	return segmentName || "Unknown Segment";
};

/**
 * Generate description based on segment
 */
const generateDescription = (segmentName, appCount) => {
	const descriptions = {
		"Direct Publishers":
			"Apps available through direct publisher relationships",
		"Performance Optimized":
			"Apps optimized for high performance metrics and engagement",
		"Relevance Focused":
			"Most relevant apps for your search criteria and target audience",
	};

	return (
		descriptions[segmentName] ||
		`Curated set of ${appCount} apps for targeted advertising`
	);
};

/**
 * Calculate total market requests across apps in a segment - FIXED
 */
const calculateTotalMarketRequests = (apps) => {
	if (!apps.length) return "0";

	const totalMarketRequests = apps.reduce((sum, app) => {
		// Try multiple possible field names for market requests
		const marketRequests =
			app.totalCprMarketRequests || // LLM search results format
			app.totalMarketRequests || // Alternative format
			app.cprMarketRequests || // Another possible format
			app.marketRequests || // Simple format
			app.totalCprMarketDemand || // Demand format
			0;

		return sum + (parseInt(marketRequests) || 0);
	}, 0);

	return formatLargeNumber(totalMarketRequests);
};

/**
 * Enhanced formatLargeNumber to handle edge cases - UPDATED
 */
const formatLargeNumber = (num) => {
	if (!num || num === 0 || isNaN(num)) return "0";

	const numValue = typeof num === "string" ? parseFloat(num) : num;

	if (isNaN(numValue) || numValue === 0) return "0";

	if (numValue >= 1000000000) {
		return `${(numValue / 1000000000).toFixed(1)}B`;
	} else if (numValue >= 1000000) {
		return `${(numValue / 1000000).toFixed(1)}M`;
	} else if (numValue >= 1000) {
		return `${(numValue / 1000).toFixed(1)}K`;
	} else if (numValue >= 1) {
		return numValue.toFixed(0);
	}
	return numValue.toString();
};

// Export additional utility functions if needed
export const appUtils = {
	formatLargeNumber,
	calculateAggregatedMetrics,
	formatSegmentName,
	generateDescription,
};

// Export individual calculation functions for testing
// ✅ UPDATED: Changed from revenue per impression to bid guidance calculations (with division by 1000)
export const appCalculations = {
	/**
	 * Calculate CTR for apps using the correct formula
	 * @param {Array} apps - Array of app objects
	 * @returns {number} CTR percentage
	 */
	calculateAppCTR: (apps) => {
		const totalClicks = apps.reduce(
			(sum, app) => sum + (app.totalCprClicks || 0),
			0,
		);
		const totalImpressionsWithClickMacro = apps.reduce(
			(sum, app) => sum + (app.totalImpressionsWithClickMacro || 0),
			0,
		);

		return totalImpressionsWithClickMacro > 0
			? (totalClicks / totalImpressionsWithClickMacro) * 100
			: 0;
	},

	/**
	 * Calculate Completion Rate for apps using the correct formula
	 * @param {Array} apps - Array of app objects
	 * @returns {number} Completion rate percentage
	 */
	calculateAppCompletionRate: (apps) => {
		const totalCompleteFirst = apps.reduce(
			(sum, app) => sum + (app.totalCompleteFirstOccurrence || 0),
			0,
		);
		const totalStartFirst = apps.reduce(
			(sum, app) => sum + (app.totalStartFirstOccurrence || 0),
			0,
		);

		return totalStartFirst > 0
			? (totalCompleteFirst * 100.0) / totalStartFirst
			: 0;
	},

	/**
	 * Calculate VTR for apps using the correct formula
	 * @param {Array} apps - Array of app objects
	 * @returns {number} VTR percentage
	 */
	calculateAppVTR: (apps) => {
		const totalCompleteFirst = apps.reduce(
			(sum, app) => sum + (app.totalCompleteFirstOccurrence || 0),
			0,
		);
		const totalStartFirst = apps.reduce(
			(sum, app) => sum + (app.totalStartFirstOccurrence || 0),
			0,
		);

		return totalStartFirst > 0
			? (totalCompleteFirst / totalStartFirst) * 100
			: 0;
	},

	/**
	 * Calculate Viewability for apps using the correct formula
	 * @param {Array} apps - Array of app objects
	 * @returns {number} Viewability percentage
	 */
	calculateAppViewability: (apps) => {
		const totalViewables = apps.reduce(
			(sum, app) => sum + (app.totalViewables || 0),
			0,
		);
		const totalViewabilityMeasures = apps.reduce(
			(sum, app) => sum + (app.totalViewabilityMeasures || 0),
			0,
		);

		return totalViewabilityMeasures > 0
			? (totalViewables / totalViewabilityMeasures) * 100
			: 0;
	},

	/**
	 * ✅ UPDATED: Calculate Average Bid Guidance for apps (DIVIDE BY 1000 FIRST)
	 * @param {Array} apps - Array of app objects
	 * @returns {number} Average bid guidance value (after dividing by 1000)
	 */
	calculateAppAverageBidGuidance: (apps) => {
		const validBidGuidanceApps = apps.filter(
			(app) =>
				app.calculated_bid_guidance !== undefined &&
				app.calculated_bid_guidance !== null &&
				!isNaN(parseFloat(app.calculated_bid_guidance)),
		);

		if (validBidGuidanceApps.length === 0) return 0;

		// ✅ DIVIDE BY 1000 FIRST before calculating average
		const totalBidGuidance = validBidGuidanceApps.reduce((sum, app) => {
			const bidGuidanceValue = parseFloat(app.calculated_bid_guidance) / 1000;
			return sum + bidGuidanceValue;
		}, 0);

		return totalBidGuidance / validBidGuidanceApps.length;
	},
};
